import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Plan } from "@/mocks/plans";
import { usePlans, formatPlanPrice } from "@/hooks/usePlans";
import PaymentModal from "./components/PaymentModal";
import JsonLd from "@/components/base/JsonLd";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://wyzon.io";

const softwareAppSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "Wyzon - WhatsApp Automation & Messaging Platform",
  "url": `${siteUrl}/plans`,
  "applicationCategory": "BusinessApplication",
  "operatingSystem": "Web",
  "description": "Wyzon helps businesses automate WhatsApp messaging, manage customer chats, send bulk campaigns, and build product catalogs with smart automation.",
  "offers": {
    "@type": "AggregateOffer",
    "priceCurrency": "EGP",
    "lowPrice": "200",
    "highPrice": "1600",
    "offerCount": "3",
  },
};

function HeroSection() {
  const { t } = useTranslation("plans");

  return (
    <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage:
            "url(https://readdy.ai/api/search-image?query=Abstract%20elegant%20flowing%20shapes%20in%20warm%20amber%20and%20green%20tones%2C%20soft%20luminous%20gradient%2C%20premium%20sophisticated%20background%2C%20modern%20luxury%20aesthetic%2C%20subtle%20golden%20light%20rays%2C%20clean%20minimal%20composition%20with%20depth&width=1920&height=1080&seq=plans-hero-01&orientation=landscape)",
        }}
      ></div>
      <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/65 via-foreground-950/45 to-foreground-950/70"></div>

      <div className="relative z-10 text-center px-4 max-w-3xl mx-auto pt-16">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-8">
          <i className="ri-price-tag-3-line text-accent-400 text-sm"></i>
          <span className="text-white/90 text-sm font-medium">{t("plans.hero.badge")}</span>
        </div>
        <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl text-white leading-tight mb-6">
          {t("plans.hero.title.line1")}
          <br />
          <span className="text-accent-400">{t("plans.hero.title.line2")}</span>
        </h1>
        <p className="text-white/70 text-lg md:text-xl max-w-xl mx-auto leading-relaxed">
          {t("plans.hero.subtitle")}
        </p>
      </div>
    </section>
  );
}

function PlansGrid() {
  const { t, i18n } = useTranslation("plans");
  const { plans, loading, error } = usePlans();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);

  const displayPrice = (plan: Plan) => formatPlanPrice(plan.amount, i18n.language);

  useEffect(() => {
    if (loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    const cards = sectionRef.current?.querySelectorAll(".plan-card");
    cards?.forEach((card, i) => {
      (card as HTMLElement).style.opacity = "0";
      (card as HTMLElement).style.transform = "translateY(40px)";
      (card as HTMLElement).style.transition = `opacity 0.7s ease ${i * 0.15}s, transform 0.7s ease ${i * 0.15}s`;
      observer.observe(card);
    });

    return () => observer.disconnect();
  }, [loading]);

  const formatNumber = (n: number) => new Intl.NumberFormat("en-US").format(n);

  return (
    <section id="plans-grid" className="py-20 md:py-28 bg-background-50 -mt-12 relative z-20">
      <div ref={sectionRef} className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8">
        {error && (
          <div className="flex items-center justify-center gap-2 mb-8 px-4 py-2.5 rounded-full bg-red-50 border border-red-200 max-w-md mx-auto">
            <i className="ri-error-warning-line text-red-500 text-sm"></i>
            <span className="text-red-600 text-xs">{error}</span>
            <span className="text-foreground-400 text-xs">|</span>
            <span className="text-foreground-500 text-xs">جارٍ عرض الأسعار المخزنة مؤقتاً</span>
          </div>
        )}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 items-stretch">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className={`rounded-2xl p-6 md:p-8 flex flex-col border bg-background-100 border-background-200/50 animate-pulse`}
              >
                <div className="mb-6">
                  <div className="h-8 w-24 bg-background-200 rounded-lg mb-2"></div>
                  <div className="h-4 w-40 bg-background-200 rounded-lg"></div>
                </div>
                <div className="mb-6">
                  <div className="h-10 w-32 bg-background-200 rounded-lg"></div>
                </div>
                <div className="h-20 bg-background-200 rounded-xl mb-6"></div>
                <div className="space-y-3 mb-8 flex-1">
                  {[0, 1, 2, 3].map((j) => (
                    <div key={j} className="h-4 bg-background-200 rounded-lg"></div>
                  ))}
                </div>
                <div className="h-12 bg-background-200 rounded-full"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 items-stretch">
            {plans.map((plan) => (
              <div
                key={plan.key}
                className={`plan-card relative rounded-2xl p-6 md:p-8 flex flex-col border transition-all duration-300 ${
                  plan.highlighted
                    ? "bg-primary-500 border-primary-500 shadow-lg scale-[1.03] z-10"
                    : "bg-background-100 border-background-200/50 hover:shadow-lg"
                }`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-semibold whitespace-nowrap">
                    {t("plans.badge.popular")}
                  </div>
                )}

                <div className="mb-6">
                  <h3
                    className={`font-heading text-2xl md:text-3xl mb-2 ${
                      plan.highlighted ? "text-background-50" : "text-foreground-950"
                    }`}
                  >
                    {t(plan.name)}
                  </h3>
                  <p
                    className={`text-sm leading-relaxed ${
                      plan.highlighted ? "text-background-50/80" : "text-foreground-600"
                    }`}
                  >
                    {t(plan.description)}
                  </p>
                </div>

                <div className="mb-6">
                  <span
                    className={`font-heading text-4xl md:text-5xl ${
                      plan.highlighted ? "text-background-50" : "text-foreground-950"
                    }`}
                  >
                    {displayPrice(plan)}
                  </span>
                  <span
                    className={`text-sm ml-1 ${
                      plan.highlighted ? "text-background-50/70" : "text-foreground-500"
                    }`}
                  >
                    / {plan.duration} {t("plans.price.perDays")}
                  </span>
                </div>

                <div
                  className={`flex flex-wrap gap-3 mb-6 p-4 rounded-xl ${
                    plan.highlighted ? "bg-background-50/15" : "bg-background-200/50"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        plan.highlighted ? "bg-background-50/20" : "bg-primary-100"
                      }`}
                    >
                      <i
                        className={`ri-message-3-line text-sm ${
                          plan.highlighted ? "text-background-50" : "text-primary-600"
                        }`}
                      ></i>
                    </div>
                    <div>
                      <p
                        className={`text-xs ${
                          plan.highlighted ? "text-background-50/60" : "text-foreground-500"
                        }`}
                      >
                        {t("plans.messages")}
                      </p>
                      <p
                        className={`text-sm font-semibold ${
                          plan.highlighted ? "text-background-50" : "text-foreground-950"
                        }`}
                      >
                        {formatNumber(plan.features.messages)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        plan.highlighted ? "bg-background-50/20" : "bg-accent-100"
                      }`}
                    >
                      <i
                        className={`ri-coins-line text-sm ${
                          plan.highlighted ? "text-background-50" : "text-accent-600"
                        }`}
                      ></i>
                    </div>
                    <div>
                      <p
                        className={`text-xs ${
                          plan.highlighted ? "text-background-50/60" : "text-foreground-500"
                        }`}
                      >
                        {t("plans.tokens")}
                      </p>
                      <p
                        className={`text-sm font-semibold ${
                          plan.highlighted ? "text-background-50" : "text-foreground-950"
                        }`}
                      >
                        {formatNumber(plan.features.tokens)}
                      </p>
                    </div>
                  </div>
                </div>

                <ul className="space-y-3 mb-8 flex-1">
                  {plan.extraFeatures.map((feat, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center">
                        <i
                          className={`text-sm ${
                            feat.included
                              ? plan.highlighted
                                ? "ri-check-line text-background-50"
                                : "ri-check-line text-primary-600"
                              : plan.highlighted
                                ? "ri-close-line text-background-50/50"
                                : "ri-close-line text-foreground-300"
                          }`}
                        ></i>
                      </div>
                      <span
                        className={`text-sm ${
                          feat.included
                            ? plan.highlighted
                              ? "text-background-50/90"
                              : "text-foreground-700"
                            : plan.highlighted
                              ? "text-background-50/50"
                              : "text-foreground-400"
                        }`}
                      >
                        {t(feat.key)}
                      </span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => setSelectedPlan(plan)}
                  className={`w-full text-center text-sm font-medium px-6 py-3.5 rounded-full transition-all cursor-pointer whitespace-nowrap ${
                    plan.highlighted
                      ? "bg-background-50 text-primary-600 hover:bg-background-100"
                      : "bg-primary-500 text-white hover:bg-primary-600"
                  }`}
                >
                  {t("plans.getStarted")} {t(plan.name)}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedPlan && (
        <PaymentModal plan={selectedPlan} onClose={() => setSelectedPlan(null)} />
      )}
    </section>
  );
}

function ComparisonTable() {
  const { t } = useTranslation("plans");
  const { plans } = usePlans();

  const formatCompact = (n: number): string => {
    if (n >= 1000000) return `${(n / 1000000).toFixed(0)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(0)}K`;
    return n.toString();
  };

  const getPlanByKey = (key: string) => plans.find((p) => p.key === key);

  const basic = getPlanByKey("basic");
  const pro = getPlanByKey("pro");
  const enterprise = getPlanByKey("enterprise");

  const basicMessages = basic ? new Intl.NumberFormat("en-US").format(basic.features.messages) : "1,000";
  const proMessages = pro ? new Intl.NumberFormat("en-US").format(pro.features.messages) : "5,000";
  const enterpriseMessages = enterprise ? new Intl.NumberFormat("en-US").format(enterprise.features.messages) : "20,000";
  const basicTokens = basic ? formatCompact(basic.features.tokens) : "500K";
  const proTokens = pro ? formatCompact(pro.features.tokens) : "2M";
  const enterpriseTokens = enterprise ? formatCompact(enterprise.features.tokens) : "10M";

  return (
    <section className="py-20 md:py-28 bg-background-100">
      <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-14">
          <span className="inline-block text-accent-600 text-xs font-medium uppercase tracking-widest mb-4">
            {t("plans.compare.badge")}
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-950 mb-4">
            {t("plans.compare.title")}
          </h2>
          <p className="text-foreground-600 text-base max-w-xl mx-auto">
            {t("plans.compare.subtitle")}
          </p>
        </div>

        <div className="overflow-x-auto rounded-2xl border border-background-200/50 bg-background-50">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-background-200/50">
                <th className="text-left py-4 px-6 text-foreground-950 font-semibold">{t("plans.compare.feature")}</th>
                <th className="text-center py-4 px-6 text-foreground-950 font-semibold">{t("plans.compare.basic")}</th>
                <th className="text-center py-4 px-6 bg-primary-50/50">
                  <span className="text-primary-600 font-semibold">{t("plans.compare.pro")}</span>
                </th>
                <th className="text-center py-4 px-6 text-foreground-950 font-semibold">{t("plans.compare.enterprise")}</th>
              </tr>
            </thead>
            <tbody>
              {[
                { label: t("plans.feature.messages"), basic: basicMessages, pro: proMessages, enterprise: enterpriseMessages },
                { label: t("plans.feature.tokens"), basic: basicTokens, pro: proTokens, enterprise: enterpriseTokens },

                { label: t("plans.feature.automation"), basic: t("plans.value.basic"), pro: t("plans.value.advanced"), enterprise: t("plans.value.fullSuite") },
                { label: t("plans.feature.analytics"), basic: t("plans.value.days7"), pro: t("plans.value.days30"), enterprise: t("plans.value.unlimitedDays") },
                { label: t("plans.feature.bulk"), basic: "\u2014", pro: "\u2713", enterprise: "\u2713" },
                { label: t("plans.feature.csv"), basic: "\u2014", pro: "\u2713", enterprise: "\u2713" },
              ].map((row, i) => (
                <tr key={i} className={`border-b border-background-200/30 ${i % 2 === 0 ? "bg-background-50" : "bg-background-100/50"}`}>
                  <td className="py-3.5 px-6 text-foreground-700 font-medium">{row.label}</td>
                  <td className="py-3.5 px-6 text-center text-foreground-600">{row.basic}</td>
                  <td className="py-3.5 px-6 text-center bg-primary-50/30">
                    <span className="text-primary-700 font-semibold">{row.pro}</span>
                  </td>
                  <td className="py-3.5 px-6 text-center text-foreground-600">{row.enterprise}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const { t } = useTranslation("plans");

  const faqs = [
    { q: t("plans.faq.q1"), a: t("plans.faq.a1") },
    { q: t("plans.faq.q2"), a: t("plans.faq.a2") },
    { q: t("plans.faq.q3"), a: t("plans.faq.a3") },
    { q: t("plans.faq.q4"), a: t("plans.faq.a4") },
  ];

  return (
    <section className="py-20 md:py-28 bg-background-50">
      <div className="max-w-3xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-14">
          <span className="inline-block text-primary-600 text-xs font-medium uppercase tracking-widest mb-4">
            {t("plans.faq.badge")}
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-950 mb-4">
            {t("plans.faq.title")}
          </h2>
        </div>
        <div className="space-y-4">
          {faqs.map((faq, i) => (
            <details key={i} className="group bg-background-100 rounded-xl border border-background-200/50 overflow-hidden">
              <summary className="flex items-center justify-between px-6 py-4 cursor-pointer list-none">
                <span className="text-foreground-950 font-medium text-base pr-4">{faq.q}</span>
                <div className="w-8 h-8 flex-shrink-0 rounded-full bg-background-200/50 flex items-center justify-center group-open:bg-primary-100 transition-colors">
                  <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform text-sm"></i>
                </div>
              </summary>
              <div className="px-6 pb-4">
                <p className="text-foreground-600 text-sm leading-relaxed">{faq.a}</p>
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  const { t } = useTranslation("plans");

  const scrollToPlans = () => {
    document.getElementById("plans-grid")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="py-20 md:py-28 bg-background-100">
      <div className="max-w-2xl mx-auto px-4 md:px-6 lg:px-8 text-center">
        <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-primary-100 flex items-center justify-center">
          <i className="ri-rocket-2-line text-primary-600 text-xl"></i>
        </div>
        <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-950 mb-4">
          {t("plans.finalCta.title")}
        </h2>
        <p className="text-foreground-600 text-base md:text-lg mb-10 leading-relaxed">
          {t("plans.finalCta.subtitle")}
        </p>
        <button
          onClick={scrollToPlans}
          className="inline-flex items-center gap-2 bg-primary-500 text-white text-sm font-medium px-8 py-3.5 rounded-full hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
        >
          {t("plans.finalCta.button")}
          <i className="ri-arrow-right-line text-sm"></i>
        </button>
      </div>
    </section>
  );
}

export default function Plans() {
  return (
    <div className="relative">
      <JsonLd data={softwareAppSchema} />
      <style>{`
        .animate-in {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }
      `}</style>
      <HeroSection />
      <PlansGrid />
      <ComparisonTable />
      <FAQSection />
      <FinalCTA />
    </div>
  );
}